/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Pill, Plus, Trash2, Download, ScanLine } from 'lucide-react';
import { createPrescription } from '@/lib/medical';
import PrescriptionOCRDialog from '@/components/telemedicine/PrescriptionOCRDialog';

const emptyDrug = () => ({
  _key: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '', dosage: '', frequency: '', duration: '', notes: '',
});

/**
 * Dialog used by doctors during/after a visit to create a digital prescription (PDF + QR).
 * Props:
 *  - patientId, appointmentId (optional), open, onOpenChange, onCreated
 */
export const PrescriptionDialog = ({ patientId, appointmentId, open, onOpenChange, onCreated }) => {
  const { t } = useTranslation();
  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [drugs, setDrugs] = useState([emptyDrug()]);
  const [saving, setSaving] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [result, setResult] = useState(null); // { pdf_base64, code }

  const reset = () => { setDiagnosis(''); setNotes(''); setDrugs([emptyDrug()]); setResult(null); };

  const updateDrug = (idx, field, value) => setDrugs(drugs.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  const addDrug = () => setDrugs([...drugs, emptyDrug()]);
  const removeDrug = (idx) => setDrugs(drugs.filter((_, i) => i !== idx));

  const canSave = drugs.every((d) => d.name.trim().length > 0);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await createPrescription({
        patient_id: patientId,
        appointment_id: appointmentId || undefined,
        diagnosis: diagnosis || undefined,
        drugs: drugs.map((d) => ({
          name: d.name,
          dosage: d.dosage || undefined,
          frequency: d.frequency || undefined,
          duration: d.duration || undefined,
          notes: d.notes || undefined,
        })),
        notes: notes || undefined,
      });
      const p = res.prescription;
      setResult({ pdf_base64: p.pdf_base64, code: p.code, qr: p.qr_code_base64 });
      toast.success(t('rxCreated'));
      onCreated?.(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally { setSaving(false); }
  };

  const download = () => {
    if (!result?.pdf_base64) return;
    const a = document.createElement('a');
    a.href = result.pdf_base64;
    a.download = `prescrizione_${result.code}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(o); if (result) reset(); } else onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin" data-testid="prescription-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Pill className="h-5 w-5 text-primary" /> {t('rxTitle')}</DialogTitle>
              <DialogDescription>{t('rxSubtitle')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="rx-diagnosis">{t('rxDiagnosis')}</Label>
                <Input id="rx-diagnosis" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder={t('rxDiagnosisPh')} data-testid="rx-diagnosis-input" />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{t('rxDrugs')} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOcrOpen(true)}
                    className="border-primary/40 text-primary hover:bg-primary/10"
                    data-testid="rx-ocr-open-btn"
                  >
                    <ScanLine className="h-3.5 w-3.5" /> Scansiona ricetta cartacea
                  </Button>
                </div>
                {drugs.map((d, idx) => (
                  <div key={d._key || `drug-${idx}`} className="rounded-2xl border border-border/60 p-3 space-y-2" data-testid={`rx-drug-row-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input placeholder={t('rxDrugName')} value={d.name} onChange={(e) => updateDrug(idx, 'name', e.target.value)} data-testid={`rx-drug-name-${idx}`} />
                        <Input placeholder={t('rxDrugDosage')} value={d.dosage} onChange={(e) => updateDrug(idx, 'dosage', e.target.value)} data-testid={`rx-drug-dosage-${idx}`} />
                        <Input placeholder={t('rxDrugFrequency')} value={d.frequency} onChange={(e) => updateDrug(idx, 'frequency', e.target.value)} data-testid={`rx-drug-freq-${idx}`} />
                        <Input placeholder={t('rxDrugDuration')} value={d.duration} onChange={(e) => updateDrug(idx, 'duration', e.target.value)} data-testid={`rx-drug-dur-${idx}`} />
                      </div>
                      {drugs.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeDrug(idx)} className="text-destructive" data-testid={`rx-remove-drug-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <Input placeholder={t('rxDrugNotes')} value={d.notes} onChange={(e) => updateDrug(idx, 'notes', e.target.value)} data-testid={`rx-drug-notes-${idx}`} />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addDrug} data-testid="rx-add-drug-btn"><Plus className="h-4 w-4" /> {t('rxAddDrug')}</Button>
              </div>
              <div>
                <Label htmlFor="rx-notes">{t('rxNotes')}</Label>
                <Textarea id="rx-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('rxNotesPh')} data-testid="rx-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
              <Button onClick={submit} disabled={!canSave || saving} data-testid="rx-submit-btn">
                {saving ? t('processing') : t('rxCreate')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-5 py-2" data-testid="rx-success-view">
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-accent/15 text-accent flex items-center justify-center">
                <Pill className="h-7 w-7" />
              </div>
              <DialogTitle className="mt-3 font-display">{t('rxCreatedTitle')}</DialogTitle>
              <DialogDescription>{t('rxCreatedDesc')}</DialogDescription>
            </div>
            <div className="rounded-2xl border border-border/60 p-4 flex items-center gap-3">
              {result.qr && <img src={result.qr} alt="QR" className="h-24 w-24 rounded-xl bg-white p-1 border" />}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Codice</div>
                <div className="font-mono font-bold text-sm truncate">{result.code}</div>
                <p className="text-xs text-muted-foreground mt-1">{t('rxCodeInfo')}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { reset(); }}>{t('rxCreateAnother')}</Button>
              <Button onClick={download} data-testid="rx-download-btn"><Download className="h-4 w-4" /> {t('rxDownloadPdf')}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      <PrescriptionOCRDialog
        open={ocrOpen}
        onOpenChange={setOcrOpen}
        patientContext={diagnosis ? `Diagnosi in corso: ${diagnosis}` : undefined}
        onExtracted={(meds) => {
          // Replace / append to drugs list: keep any existing non-empty rows, then add extracted ones.
          const nonEmpty = drugs.filter((d) => d.name?.trim());
          const merged = [
            ...nonEmpty,
            ...meds.map((m) => ({
              _key: (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ...m,
            })),
          ];
          setDrugs(merged.length ? merged : [emptyDrug()]);
          toast.success(`${meds.length} farmac${meds.length === 1 ? 'o importato' : 'i importati'}`);
        }}
      />
    </Dialog>
  );
};

export default PrescriptionDialog;
